import { useState, useCallback, useEffect } from "react";
import { Product } from "@/components/business/products/ProductCard";
import { productCache, type CacheMetrics } from "@/services";

interface UseProductCacheReturn {
  // 🎯 OPERACIONES DE PRODUCTOS
  getProduct: (id: string) => Promise<Product | null>;
  getProducts: (ids: string[]) => Promise<Product[]>;
  setProduct: (id: string, product: Product) => void;
  invalidateProduct: (id: string) => void;
  clearCache: () => void;

  // 📊 ESTADO Y MÉTRICAS
  isLoading: boolean;
  error: string | null;
  cacheMetrics: CacheMetrics;
  cacheStatus: {
    size: number;
    maxSize: number;
    hitRate: number;
    isOnline: boolean;
  };

  // 🎯 UTILIDADES
  isOnline: boolean;
  refreshCache: () => void;
}

export function useProductCache(): UseProductCacheReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cacheMetrics, setCacheMetrics] = useState<CacheMetrics>({
    totalCached: 0,
    cacheHits: 0,
    cacheMisses: 0,
    hitRate: 0,
    averageAccessTime: 0,
    memoryUsage: 0,
  });
  const [cacheStatus, setCacheStatus] = useState({
    size: 0,
    maxSize: 50,
    hitRate: 0,
    isOnline: navigator.onLine,
  });

  // 🔄 ACTUALIZAR MÉTRICAS Y ESTADO
  const updateMetrics = useCallback(() => {
    setCacheMetrics(productCache.getMetrics());
    setCacheStatus(productCache.getCacheStatus());
  }, []);

  // 🔄 MONITOREAR ESTADO DEL CACHE
  useEffect(() => {
    const interval = setInterval(updateMetrics, 1000);
    return () => clearInterval(interval);
  }, [updateMetrics]);

  // 🔄 MONITOREAR ESTADO ONLINE/OFFLINE
  useEffect(() => {
    const handleOnline = () => {
      setCacheStatus(prev => ({ ...prev, isOnline: true }));
      console.log("🌐 Conexión restaurada");
    };

    const handleOffline = () => {
      setCacheStatus(prev => ({ ...prev, isOnline: false }));
      console.log("📱 Modo offline activado");
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // 🎯 OBTENER PRODUCTO
  const getProduct = useCallback(async (id: string): Promise<Product | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const product = await productCache.getProduct(id);
      updateMetrics();
      return product;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Error desconocido";
      setError(errorMessage);
      console.error("Error obteniendo producto:", err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [updateMetrics]);

  // 🎯 OBTENER MÚLTIPLES PRODUCTOS
  const getProducts = useCallback(async (ids: string[]): Promise<Product[]> => {
    setIsLoading(true);
    setError(null);

    try {
      const products = await productCache.getProducts(ids);
      updateMetrics();
      return products;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Error desconocido";
      setError(errorMessage);
      console.error("Error obteniendo productos:", err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [updateMetrics]);

  // 🎯 ESTABLECER PRODUCTO EN CACHE
  const setProduct = useCallback((id: string, product: Product) => {
    productCache.setProduct(id, product);
    updateMetrics();
  }, [updateMetrics]);

  // 🎯 INVALIDAR PRODUCTO
  const invalidateProduct = useCallback((id: string) => {
    productCache.invalidateProduct(id);
    updateMetrics();
  }, [updateMetrics]);

  // 🎯 LIMPIAR CACHE
  const clearCache = useCallback(() => {
    productCache.clearCache();
    updateMetrics();
  }, [updateMetrics]);

  // 🎯 REFRESH CACHE
  const refreshCache = useCallback(() => {
    updateMetrics();
  }, [updateMetrics]);

  return {
    // 🎯 OPERACIONES DE PRODUCTOS
    getProduct,
    getProducts,
    setProduct,
    invalidateProduct,
    clearCache,

    // 📊 ESTADO Y MÉTRICAS
    isLoading,
    error,
    cacheMetrics,
    cacheStatus,

    // 🎯 UTILIDADES
    isOnline: navigator.onLine,
    refreshCache,
  };
}

// 🎯 HOOK ESPECÍFICO PARA UN PRODUCTO
export function useProduct(productId: string) {
  const { getProduct, isLoading, error, cacheMetrics } = useProductCache();
  const [product, setProduct] = useState<Product | null>(null);

  // 🔄 CARGAR PRODUCTO
  const loadProduct = useCallback(async () => {
    if (!productId) return;

    const fetchedProduct = await getProduct(productId);
    setProduct(fetchedProduct);
  }, [productId, getProduct]);

  // 🔄 CARGAR PRODUCTO AL MONTAR
  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  return {
    product,
    isLoading,
    error,
    cacheMetrics,
    refresh: loadProduct,
  };
}

// 🎯 HOOK PARA MÚLTIPLES PRODUCTOS
export function useProducts(productIds: string[]) {
  const { getProducts, isLoading, error, cacheMetrics } = useProductCache();
  const [products, setProducts] = useState<Product[]>([]);

  // 🔄 CARGAR PRODUCTOS
  const loadProducts = useCallback(async () => {
    if (!productIds.length) return;

    const fetchedProducts = await getProducts(productIds);
    setProducts(fetchedProducts);
  }, [productIds, getProducts]);

  // 🔄 CARGAR PRODUCTOS AL MONTAR
  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  return {
    products,
    isLoading,
    error,
    cacheMetrics,
    refresh: loadProducts,
  };
} 